import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout, retry } from 'rxjs/operators';
import { NetworkErrorService } from '../services/network-error.service';

@Injectable()
export class NetworkInterceptor implements HttpInterceptor {

    constructor(private networkErrorService: NetworkErrorService) { }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        // The SomosKycV1 OCR pre-check runs Textract on the uploaded document side(s) — inherently
        // slower than a normal API call. Give it a longer budget (but under the ~30s API Gateway
        // ceiling so a clean client timeout fires before the gateway's 504), and treat ANY failure as
        // silent — the caller fails open and proceeds, so no retry/error modal should ever show.
        const isLongRunning = request.url.includes('/somos_kyc_ocr_extract');
        // CPK-5103: the is_document check is fail-open as well (its caller races a shorter
        // timeout of its own and lets the image through) — never a retry or error modal.
        const isFailOpen = isLongRunning || request.url.includes('/kyc_is_document');
        const timeoutMs = isLongRunning ? 28000 : 20000;

        return next.handle(request).pipe(
            timeout(timeoutMs),
            retry({
                delay: (error) => {
                    if (error instanceof TimeoutError) {
                        if (isFailOpen) {
                            // Fail open — don't show the retry modal for a best-effort pre-check.
                            return throwError(() => error);
                        }
                        this.networkErrorService.showTimeoutError();
                        // Wait for the user to click retry
                        return this.networkErrorService.retry$;
                    }
                    // If it's not a timeout error, propagate it to catchError
                    return throwError(() => error);
                }
            }),
            catchError((error: HttpErrorResponse | TimeoutError) => {
                console.log('NetworkInterceptor caught error:', error); // DEBUG

                // Best-effort pre-check: never surface an error modal — the caller fails open.
                if (isFailOpen) {
                    return throwError(() => error);
                }

                if (error instanceof HttpErrorResponse) {
                    const status = error.status;
                    if (status >= 500 || status === 0) {
                        console.log('Showing generic error modal for status:', status); // DEBUG
                        this.networkErrorService.showGenericError();
                    } else {
                        console.log('Skipping generic error modal for client error status:', status); // DEBUG
                    }
                }

                return throwError(() => error);
            })
        );
    }
}
